/**
 * ABK Cart Upsell - Hotel Upgrade
 *
 * Two modes:
 * 1. Inline upsell (existing): button click swaps variant via Cart API.
 * 2. Modal upsell (new): triggered by arrival date selection in traveler form.
 *    Maps date → hotel SKU, fetches variant data, shows persuasive modal.
 */
(function () {
  /* ── Constants ── */
  const HOTEL_SKU_MAP = {
    june: 'ABK-CARD-HOTEL-LUG-7',
    july: 'ABK-CARD-HOTEL-LUG-7',
    august: 'ABK-CARD-HOTEL-AGO-7',
  };

  const MODAL_DISMISSED_KEY = 'abk-upsell-modal-dismissed';

  /* ── State ── */
  let productData = null;
  let modalShown = false;

  /* ── Init ── */
  function init() {
    console.log('[ABK Upsell] Init');

    // Inline upsell: event delegation for upgrade button
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-upsell-upgrade-btn]');
      if (btn && !btn.disabled) handleUpgrade(btn);
    });

    // Modal upsell: listen for date selection
    document.addEventListener('abk:arrival-date-selected', handleDateSelected);

    // Modal: close handlers
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-upsell-modal-close]') || e.target.closest('[data-upsell-modal-dismiss]')) {
        closeModal(false);
      }
      // Overlay click disabled — modal closes only via X or dismiss button
    });

    // Modal: accept handler
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-upsell-modal-accept]');
      if (btn && !btn.disabled) handleModalAccept(btn);
    });

    // Escape key closes modal
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal(false);
    });
  }

  /* ── Date → Modal flow ── */
  async function handleDateSelected(e) {
    const date = e.detail?.date;
    console.log('[ABK Upsell] Date selected:', date, '| modalShown:', modalShown);

    if (!date || modalShown) {
      console.log('[ABK Upsell] Skipped: no date or modal already shown');
      return;
    }

    // Check if modal was already dismissed this session
    if (sessionStorage.getItem(MODAL_DISMISSED_KEY)) {
      console.log('[ABK Upsell] Skipped: modal was dismissed this session');
      return;
    }

    // Check if user already has a hotel variant in cart
    const container = document.querySelector('[data-cart-upsell-hotel]');
    if (!container) {
      console.log('[ABK Upsell] Skipped: no [data-cart-upsell-hotel] container found');
      return;
    }

    const baseSku = container.dataset.baseVariantSku || '';
    console.log('[ABK Upsell] Base SKU:', baseSku);
    if (baseSku.toUpperCase().includes('HOTEL')) {
      console.log('[ABK Upsell] Skipped: already has hotel variant');
      return;
    }

    // Map date to hotel SKU
    const targetSku = getHotelSkuForDate(date);
    console.log('[ABK Upsell] Target SKU for date:', targetSku);
    if (!targetSku) {
      console.log('[ABK Upsell] Skipped: no hotel SKU for this date');
      return;
    }

    // Fetch product data
    const handle = container.dataset.productHandle;
    console.log('[ABK Upsell] Product handle:', handle);
    if (!handle) {
      console.log('[ABK Upsell] Skipped: no product handle');
      return;
    }

    try {
      if (!productData) {
        console.log('[ABK Upsell] Fetching product data from /products/' + handle + '.js');
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) {
          console.log('[ABK Upsell] Fetch failed with status:', res.status);
          return;
        }
        productData = await res.json();
        console.log('[ABK Upsell] Product data loaded, variants:', productData.variants.length);
        console.log('[ABK Upsell] Available SKUs:', productData.variants.map(v => v.sku));
      }

      // Find the hotel variant by SKU
      const hotelVariant = productData.variants.find(
        (v) => v.sku && v.sku.toUpperCase() === targetSku.toUpperCase() && v.available
      );
      if (!hotelVariant) {
        console.log('[ABK Upsell] Hotel variant not found for SKU:', targetSku);
        return;
      }
      console.log('[ABK Upsell] Hotel variant found:', hotelVariant.id, hotelVariant.sku, 'price:', hotelVariant.price);

      // Find the base variant (currently in cart)
      const baseVariantId = parseInt(container.dataset.baseVariantId, 10);
      const baseVariant = productData.variants.find((v) => v.id === baseVariantId);
      if (!baseVariant) {
        console.log('[ABK Upsell] Base variant not found for ID:', baseVariantId);
        return;
      }
      console.log('[ABK Upsell] Base variant:', baseVariant.id, baseVariant.sku, 'price:', baseVariant.price);

      // Calculate pricing
      const basePrice = baseVariant.price / 100;
      const hotelPrice = hotelVariant.price / 100;
      const delta = hotelPrice - basePrice;
      const perNight = delta / 7;
      console.log('[ABK Upsell] Pricing — base:', basePrice, 'hotel:', hotelPrice, 'delta:', delta, 'perNight:', perNight);

      // Find selling plan for hotel variant if base has one
      let hotelSellingPlanId = null;
      const hasSellingPlan = container.dataset.hasSellingPlan === 'true';
      const sellingPlanName = container.dataset.sellingPlanName || '';
      console.log('[ABK Upsell] Selling plan — has:', hasSellingPlan, 'name:', sellingPlanName);

      if (hasSellingPlan) {
        // Product JSON allocations have selling_plan_id directly (no nested .name)
        // Use the first allocation's selling_plan_id from the hotel variant
        const allocations = hotelVariant.selling_plan_allocations || [];
        console.log('[ABK Upsell] Hotel variant selling_plan_allocations:', JSON.stringify(allocations));

        if (allocations.length > 0) {
          hotelSellingPlanId = allocations[0].selling_plan_id;
        }

        console.log('[ABK Upsell] Selling plan match:', hotelSellingPlanId);
      }

      console.log('[ABK Upsell] Showing modal...');
      // Show modal
      showModal({
        hotelVariant,
        baseVariantId,
        delta,
        perNight,
        hotelPrice,
        hasSellingPlan,
        hotelSellingPlanId,
        depositAmount: hotelPrice * 0.5,
        lineKey: container.dataset.lineKey,
        quantity: parseInt(container.dataset.baseQuantity, 10) || 1,
      });
    } catch (err) {
      console.error('[ABK Upsell] Modal data fetch failed:', err);
    }
  }

  function getHotelSkuForDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const month = date.getMonth(); // 0-indexed: 5=June, 6=July, 7=August

    if (month === 5) return HOTEL_SKU_MAP.june;
    if (month === 6) return HOTEL_SKU_MAP.july;
    if (month === 7) return HOTEL_SKU_MAP.august;
    return null;
  }

  /* ── Modal display ── */
  function showModal(data) {
    console.log('[ABK Upsell] showModal() called with data:', JSON.stringify(data, null, 2));
    const modal = document.querySelector('[data-upsell-modal]');
    if (!modal) {
      console.log('[ABK Upsell] Modal element not found in DOM!');
      return;
    }

    // Populate dynamic values
    const fmt = (n) => n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    const fmtRound = (n) => Math.floor(n).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const perNightEl = modal.querySelector('[data-upsell-modal-per-night]');
    if (perNightEl) perNightEl.textContent = fmtRound(data.perNight);

    const deltaEl = modal.querySelector('[data-upsell-modal-delta]');
    if (deltaEl) deltaEl.textContent = fmt(data.delta);

    const depositEl = modal.querySelector('[data-upsell-modal-deposit]');
    const depositAmountEl = modal.querySelector('[data-upsell-modal-deposit-amount]');
    if (depositEl && depositAmountEl && data.hasSellingPlan) {
      depositAmountEl.textContent = fmtRound(data.depositAmount);
      depositEl.hidden = false;
    } else if (depositEl) {
      depositEl.hidden = true;
    }

    // Store data on accept button for the upgrade handler
    const acceptBtn = modal.querySelector('[data-upsell-modal-accept]');
    if (acceptBtn) {
      acceptBtn.dataset.upgradeVariantId = data.hotelVariant.id;
      acceptBtn.dataset.baseVariantId = data.baseVariantId;
      acceptBtn.dataset.lineKey = data.lineKey;
      acceptBtn.dataset.quantity = data.quantity;
      if (data.hotelSellingPlanId) {
        acceptBtn.dataset.upgradeSellingPlanId = data.hotelSellingPlanId;
      } else {
        delete acceptBtn.dataset.upgradeSellingPlanId;
      }
    }

    // Populate variant image
    const imageContainer = modal.querySelector('[data-upsell-modal-image]');
    if (imageContainer) {
      const img = imageContainer.querySelector('img');
      const variantImage = data.hotelVariant.featured_image?.src || (productData && productData.featured_image);
      console.log('[ABK Upsell] Variant image URL:', variantImage);
      if (img && variantImage) {
        img.src = variantImage;
        img.alt = data.hotelVariant.title || 'Hotel upgrade';
        imageContainer.style.display = '';
      } else if (imageContainer) {
        imageContainer.style.display = 'none';
      }
    }

    // Show with CSS transition (is-active class)
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Force reflow before adding class for transition
    modal.offsetHeight;
    modal.classList.add('is-active');
    modalShown = true;
    console.log('[ABK Upsell] Modal is now visible');
  }

  function closeModal(accepted) {
    console.log('[ABK Upsell] closeModal() accepted:', accepted);
    const modal = document.querySelector('[data-upsell-modal]');
    if (!modal || !modal.classList.contains('is-active')) return;

    modal.classList.remove('is-active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (!accepted) {
      // Mark as dismissed so it doesn't show again
      sessionStorage.setItem(MODAL_DISMISSED_KEY, 'true');
    }
  }

  /* ── Modal accept → upgrade ── */
  async function handleModalAccept(btn) {
    console.log('[ABK Upsell] Modal accepted! Starting upgrade...');
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    const upgradeVariantId = parseInt(btn.dataset.upgradeVariantId, 10);
    const lineKey = btn.dataset.lineKey;
    const quantity = parseInt(btn.dataset.quantity, 10) || 1;
    const upgradeSellingPlanId = btn.dataset.upgradeSellingPlanId || null;
    console.log('[ABK Upsell] Upgrade params — variantId:', upgradeVariantId, 'lineKey:', lineKey, 'qty:', quantity, 'sellingPlan:', upgradeSellingPlanId);

    try {
      // Step 1: Add upgrade variant
      const addItem = { id: upgradeVariantId, quantity };
      if (upgradeSellingPlanId) {
        addItem.selling_plan = parseInt(upgradeSellingPlanId, 10);
      }

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [addItem] }),
      });

      const addData = await addRes.json();
      if (!addRes.ok) {
        throw new Error(addData?.description || addData?.message || 'Failed to add upgrade variant');
      }
      console.log('[ABK Upsell] Upgrade variant added successfully');

      // Step 2: Fetch fresh cart to get the correct line key for the base variant
      const baseVarId = parseInt(btn.dataset.baseVariantId, 10);
      const cartRes = await fetch('/cart.js');
      const cartData = await cartRes.json();
      const baseItem = cartData.items.find(
        (item) => item.variant_id === baseVarId
      );

      if (baseItem) {
        console.log('[ABK Upsell] Removing base variant with key:', baseItem.key);
        const removeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: baseItem.key, quantity: 0 }),
        });

        if (!removeRes.ok) {
          console.error('[ABK Upsell] Failed to remove base variant');
        }
      } else {
        console.log('[ABK Upsell] Base variant not found in cart, skipping removal');
      }

      // Close modal and reload
      closeModal(true);
      window.location.href = window.location.pathname;
    } catch (err) {
      console.error('[ABK Upsell] Modal upgrade failed:', err);
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.classList.remove('loading');
      const textEl = btn.querySelector('[data-upsell-modal-accept-text]');
      if (textEl) {
        textEl.textContent = 'Errore, riprova';
        textEl.style.opacity = '1';
      }
      // Force HoverButton to reset its internal state
      const btnFill = btn.querySelector('[data-fill]');
      if (btnFill) btnFill.style.transform = '';
    }
  }

  /* ── Inline upsell (existing logic, unchanged) ── */
  async function handleUpgrade(btn) {
    const container = btn.closest('[data-cart-upsell-hotel]');
    if (!container) return;

    const lineKey = container.dataset.lineKey;
    const upgradeVariantId = parseInt(container.dataset.upgradeVariantId, 10);
    const quantity = parseInt(container.dataset.baseQuantity, 10) || 1;
    const upgradeSellingPlanId = container.dataset.upgradeSellingPlanId || null;

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    try {
      const addItem = { id: upgradeVariantId, quantity: quantity };
      if (upgradeSellingPlanId) {
        addItem.selling_plan = upgradeSellingPlanId;
      }

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [addItem] }),
      });

      const addData = await addRes.json();
      if (!addRes.ok) {
        throw new Error(addData?.description || addData?.message || 'Failed to add upgrade variant');
      }

      const removeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });

      if (!removeRes.ok) throw new Error('Failed to remove base variant');

      window.location.href = window.location.pathname;
    } catch (err) {
      console.error('[ABK Upsell] Upgrade failed:', err);
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.querySelector('[data-upsell-btn-text]').textContent = 'Errore, riprova';
    }
  }

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
